import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { isFileReferenceUrl } from './FileLink';

export type ResolvedFileUrl =
  | { status: 'ready'; src: string }
  | { status: 'loading' }
  | { status: 'private' }
  | { status: 'failed' };

const PRIVATE_STATE: ResolvedFileUrl = { status: 'private' };
const FAILED_STATE: ResolvedFileUrl = { status: 'failed' };

function fileIdFromRef(url: string): string {
  return url.replace(/^\/api\/files\//, '');
}

function isForbidden(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    (err as { response?: { status?: number } }).response?.status === 403
  );
}

/**
 * Exchanges a private-file reference for a short-lived signed URL via
 * GET /agent-files/:fileId. 403 means the file belongs to someone else
 * (`private`); anything else that fails means the file is gone (`failed`).
 */
export async function resolveFileReference(
  url: string,
  getAccessTokenSilently: () => Promise<string>,
): Promise<ResolvedFileUrl> {
  try {
    const token = await getAccessTokenSilently();
    const res = await api.get<{ success: boolean; downloadUrl?: string }>(
      `/agent-files/${fileIdFromRef(url)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.data.success && res.data.downloadUrl) {
      return { status: 'ready', src: res.data.downloadUrl };
    }
    return FAILED_STATE;
  } catch (err) {
    return isForbidden(err) ? PRIVATE_STATE : FAILED_STATE;
  }
}

/**
 * Turns a URL that may be a private-file reference (`/api/files/{fileId}`)
 * into something an `<img>`/`<video>` can load. Plain URLs are returned as-is;
 * references are exchanged for a short-lived signed URL via GET
 * /agent-files/:fileId with the user's token.
 *
 * A viewer who is not signed in (public share pages) gets `private` straight
 * away, without any request; a signed-in viewer who does not own the file
 * gets `private` from the server's 403.
 */
export function useResolvedFileUrl(url: string): ResolvedFileUrl {
  const resolved = useResolvedFileUrls([url]);
  return resolved[url] ?? { status: 'loading' };
}

/**
 * Same as `useResolvedFileUrl`, for a list of URLs (e.g. a card's main image
 * plus its thumbnails). Returns one state per distinct URL.
 */
export function useResolvedFileUrls(
  urls: string[],
): Record<string, ResolvedFileUrl> {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const key = urls.join('\n');
  const initial = () => resolveSync(urls, isAuthenticated, isLoading);
  const [state, setState] = useState<Record<string, ResolvedFileUrl>>(initial);

  useEffect(() => {
    const sync = resolveSync(urls, isAuthenticated, isLoading);
    setState(sync);
    const pending = urls.filter((u) => sync[u]?.status === 'loading');
    if (pending.length === 0 || isLoading || !isAuthenticated) return undefined;
    let cancelled = false;
    for (const u of pending) {
      resolveFileReference(u, getAccessTokenSilently).then((r) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, [u]: r }));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [key, isAuthenticated, isLoading, getAccessTokenSilently]);

  return state;
}

function resolveSync(
  urls: string[],
  isAuthenticated: boolean,
  isLoading: boolean,
): Record<string, ResolvedFileUrl> {
  const out: Record<string, ResolvedFileUrl> = {};
  for (const u of urls) {
    if (!u) continue;
    if (!isFileReferenceUrl(u)) out[u] = { status: 'ready', src: u };
    else if (isLoading) out[u] = { status: 'loading' };
    else if (!isAuthenticated) out[u] = PRIVATE_STATE;
    else out[u] = { status: 'loading' };
  }
  return out;
}
